import { useEffect, useMemo, useState } from 'react';
import { roomInfo } from '../api';
import { type ServerClock, updateClock } from '../clock';
import { storeMonitorKey, storedMonitorKey } from '../identity';
import { navigate } from '../router';
import { type RoomAuth, connectRoom } from '../socket';
import { useRoom } from '../useRoom';
import { useVerification } from '../useVerification';
import { TOO_MANY } from './MonitorPage';
import { TableView } from './TableView';

/** The operator's read-only table at `/<path>/r/<roomId>`; without a stored key it goes back to the key form. */
export function MonitorTablePage({ path, roomId, connect = connectRoom }: { path: string; roomId: string; connect?: typeof connectRoom }) {
  const key = storedMonitorKey();
  useEffect(() => { if (key === null) navigate(`/${path}`); }, [key, path]);
  return key === null ? null : <MonitorTable path={path} roomId={roomId} monitorKey={key} connect={connect} />;
}

function MonitorTable({ path, roomId, monitorKey, connect }: { path: string; roomId: string; monitorKey: string; connect: typeof connectRoom }) {
  const auth = useMemo<RoomAuth>(() => ({ monitorKey }), [monitorKey]);
  const [exists, setExists] = useState<boolean | null>(null);
  useEffect(() => { roomInfo(roomId).then(info => setExists(info.exists)).catch(() => setExists(true)); }, [roomId]);
  const room = useRoom(roomId, auth, connect);
  const verification = useVerification(room.snapshot);
  const clock: ServerClock = useMemo(() => updateClock({ offset: 0 }, room.snapshot?.serverTime ?? Date.now()), [room.snapshot?.serverTime]);
  useEffect(() => { if (room.refusal === 'bad_key') { storeMonitorKey(null); navigate(`/${path}`); } }, [room.refusal, path]);
  const back = <a href={`/${path}`} onClick={e => { e.preventDefault(); navigate(`/${path}`); }}>← ALL GAMES</a>;

  if (exists === false || room.refusal === 'room_not_found') {
    return <main className="simple-page"><h1>This game does not exist</h1><p>The link may be wrong, or the server was restarted.</p>{back}</main>;
  }
  if (room.refusal === 'rate_limited') return <main className="simple-page"><h1>{TOO_MANY}</h1>{back}</main>;
  if (room.refusal) return <main className="simple-page"><h1>Connection refused</h1><p>{room.refusal}</p>{back}</main>;
  if (!room.snapshot) return <main className="connecting">Connecting…</main>;
  return (
    <div className="monitor-table">
      <div className="monitor-bar">{back}<span>MONITOR · {roomId}</span></div>
      {room.status === 'reconnecting' && <div className="banner warn">Reconnecting…</div>}
      <TableView snapshot={room.snapshot} verification={verification} clock={clock} act={room.act} roomId={roomId} monitor />
    </div>
  );
}
